import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  EMPTY,
  filter,
  from,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  GradeLesson,
  TeacherNotes,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { NotesType } from '../../../../models/lesson.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { UserModel } from '../../../../models/user.model';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { SchoolGradeLessonService } from '../../../../services/grade-lesson.school.service';
import * as gradeLessonActions from '../../../../state/actions/grade-lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as lessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';
import { RichTextEditorComponent } from '../../../components/quill-editor/rich-text-editor/rich-text-editor.component';
import { TextEditorComponent } from '../../../components/text-editor/text-editor.component';

@Component({
  selector: 'app-view-lesson',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnFabComponent,
    AsyncPipe,
    RouterLink,
    RichTextEditorComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    MomentPipe,
    BtnRouteComponent,
  ],
  templateUrl: './view-lesson.component.html',
  styleUrl: './view-lesson.component.css',
})
export class ViewLessonComponent implements OnInit, OnDestroy {
  pdfSrc: Uint8Array | Blob | string = ''; // Will be set to Blob/Uint8Array

  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  store: Store<AppState> = inject(Store<AppState>);
  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);
  lessonService: SchoolGradeLessonService = inject(SchoolGradeLessonService);
  @ViewChild('textEditor', { static: false }) textEditor!: TextEditorComponent;
  private dialog: MatDialog = inject(MatDialog);
  private actions$: Actions = inject(Actions);

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  facultyId: string | null = this.route.snapshot.paramMap.get('facultyId');
  studentId: string | null = this.route.snapshot.paramMap.get('studentId');
  lessonId: string | null = this.route.snapshot.paramMap.get('lessonId');

  academicYear: string | null =
    this.route.snapshot.queryParamMap.get('academicYear');

  htmlData: string = '';

  user$: Observable<UserModel | null> = new Observable();
  group$: Observable<Group | null | undefined> = new Observable();
  grade$: Observable<Grade | null | undefined> = new Observable();
  gradeLesson: GradeLesson | null | undefined;

  myRoles$: Observable<any> = new Observable();

  loading: boolean = false;
  readonly: boolean = true;

  working: boolean = false;

  membershipStatus = MembershipStatus;
  noteTypes = NotesType;

  exit$: Subject<void> = new Subject<void>();

  constructor(private http: HttpClient) {
    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params: any) => {
      this.groupId = params.get('id');
      this.facultyId = params.get('facultyId');
      this.lessonId = params.get('lessonId');
    });
    this.route.queryParamMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params: any) => {
        this.academicYear = params.get('academicYear');
      });

    this.user$ = this.store.select((state) => state.user.user);

    this.group$ = this.store.select(groupSelector.getSelectedGroup);
  }

  ngOnInit(): void {
    this.store
      .select(lessonSelector.getGradeLessonById(this.lessonId!))
      .pipe(
        take(1),
        tap((lesson) => {
          if (!lesson) {
            this.store.dispatch(
              gradeLessonActions.getGradeLesson({
                groupId: this.groupId!,
                gradeLessonId: this.lessonId!,
              }),
            );
          }
        }),
        filter((lesson): lesson is GradeLesson => !!lesson),
        switchMap((lesson) => {
          this.gradeLesson = lesson;
          // this.logger.debug(
          //   `Grade Lesson: ${JSON.stringify(this.gradeLesson, null, 2)}`,
          // );
          if (lesson.notesUrl && lesson.notesType === NotesType.text) {
            this.loading = true;
            // Convert your Promise to an Observable using 'from' or update service to return Observable
            return from(
              this.lessonService.getNotes(`${lesson.notesUrl}?v=${Date.now()}`),
            ).pipe(
              tap((data) => {
                this.htmlData = JSON.stringify(data);
                this.loading = false;
              }),
              catchError((error) => {
                this.toastr.error(
                  'Error occurred while fetching notes from our servers. Please try again later.',
                  'Error fetching Notes',
                );
                this.loading = false;
                return EMPTY; // Stop the error from killing the main stream
              }),
            );
          }
          return of(lesson); // Return the lesson if no notes need fetching
        }),
      )
      .subscribe();

    this.grade$ = this.store.select(
      gradeSelector.getGradeByTeacherId({
        teacherId: this.facultyId!,
        groupId: this.groupId!,
        academicYear: Number(this.academicYear!),
      }),
    );

    this.actions$
      .pipe(ofType(gradeLessonActions.updateSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.working = false;
      });

    this.actions$
      .pipe(ofType(gradeLessonActions.updateFailure), takeUntil(this.exit$))
      .subscribe(() => {
        this.working = false;
        this.toastr.error(
          'Error occurred while updating notes. Please try again later.',
          'Error updating Notes',
        );
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onDelete(noteId: string) {
    if (this.working) return;

    this.working = true;

    const teacherNotes: TeacherNotes[] | undefined =
      this.gradeLesson?.teacherNotes?.filter((note) => note.id !== noteId);

    this.store.dispatch(
      gradeLessonActions.update({
        groupId: this.groupId!,
        lessonId: this.lessonId!,
        gradeLesson: { ...this.gradeLesson, teacherNotes: teacherNotes },
      }),
    );
  }

  onEditNotes(item: TeacherNotes) {
    this.router.navigate(['editnotes', item.id], {
      relativeTo: this.route,
    });
  }
}
